import { useEffect, useState } from 'react';
import Modal from 'react-modal';
import useDeleteImage from '../../../hooks/useDeleteImage';
import useObjectElements from '../../../hooks/useObjectElements';
import EditImageView from '../../EditImage';
import Checkbox from '../../Checkbox';
import MyDatePicker from '../../MyDatePicker';
import { ButtonsWrapper, CloseButton, ModalTitle } from '../../StandardizationProject/CreateEditModuleModal/styles';
import { Row, StyledActionBtn } from '../../StandardizationProject/styles';
import useAddProject from '../hooks/useAddProject';
import useDeleteProject from '../hooks/useDeleteProject';
import useUpdateProject from '../hooks/useUpdateProject';
import {
  Wrapper,
  ContentWrapper,
  InputsWrapper,
  ImageWrapper,
  Input,
  InputsGrid,
  GridItem,
  Label,
  NotesInput,
  RemoveProjectBtn,
  InputComment,
} from './styles';

const formDefaultObject = {
  name: '',
  number: '',
  location: '',
  client: '',
  oilFlowRate: 0,
  gasRate: 0,
  waterInjection: 0,
  gasInjection: false,
  gasExport: false,
  date: null,
  notes: '',
  imageId: null,
};

const numberFields = ['oilFlowRate', 'gasRate', 'waterInjection'];

const CreateEditProjectModal = ({ project, onModalClose, isAdmin }) => {
  const isEditMode = project !== null;
  const { deleteImage } = useDeleteImage();
  const { addProject } = useAddProject();
  const { updateProject } = useUpdateProject();
  const { deleteProject } = useDeleteProject();
  const [uploadedImagesIds, setUploadedImagesIds] = useState([]);
  const [projectNumberValid, setProjectNumberValid] = useState(false);
  const {
    object: formData,
    setObjectValue: setFormField,
    setMultipleObjectValues: setMultipleFormFields,
  } = useObjectElements({ initialValues: { ...formDefaultObject } });
  // mode - discard, update, add
  const deleteImagesNotNeeded = async (mode, imageId) => {
    let imageIdsToDelete = [];
    if (mode === 'add' || mode === 'update') {
      imageIdsToDelete = uploadedImagesIds.filter((id) => id !== imageId);
    } else {
      // discard
      imageIdsToDelete = uploadedImagesIds;
    }
    Promise.all(imageIdsToDelete.map((id) => deleteImage(id)));
  };

  const onDiscardChanges = () => {
    deleteImagesNotNeeded();
  };

  const formatFormData = () => {
    const formatedFormData = {};
    for (const [key, val] of Object.entries(formData)) {
      if (numberFields.includes(key)) {
        formatedFormData[key] = parseFloat(val);
      } else {
        formatedFormData[key] = val;
      }
    }
    return formatedFormData;
  };

  const handleAddProject = () => {
    const formatedFormData = formatFormData();

    addProject(formatedFormData, {
      onSuccess: (data) => {
        deleteImagesNotNeeded('add', data.imageId);
        onModalClose && onModalClose();
      },
    });
  };

  const handleUpdateProject = () => {
    const formatedFormData = formatFormData();

    updateProject(project.id, formatedFormData, {
      onSuccess: (data) => {
        deleteImagesNotNeeded('update', data.imageId);
        onModalClose && onModalClose();
      },
    });
  };

  const handleRemoveProject = () => {
    deleteProject(project.id, {
      onSuccess: () => {
        onModalClose && onModalClose();
      },
    });
  };

  const handleModalClose = () => {
    onDiscardChanges();
    onModalClose && onModalClose();
  };

  useEffect(() => project && setMultipleFormFields(project), []);

  const validateProjectNumber = (val) => {
    setProjectNumberValid(val && val.length === 7 && val.match("\\w{2,2}\\d{5,5}"));
  };

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc
      onRequestClose={handleModalClose}>
      <Wrapper>
        <ModalTitle>{isEditMode ? 'Edit project:' : 'Add new project:'}</ModalTitle>
        <CloseButton onClick={handleModalClose} />
        <ContentWrapper>
          <div style={{ display: 'flex' }}>
            <Row>
              <InputsWrapper>
                <Input
                  value={formData.name}
                  placeholder='Project Name/Description'
                  onChange={(e) => setFormField('name', e.target.value)}
                />
                <Input
                  value={formData.number}
                  placeholder='Project Number'
                  onChange={(e) => {
                    validateProjectNumber(e.target.value);
                    setFormField('number', e.target.value);
                  }}
                />
                <InputComment>Project number format should be 'AA00000'</InputComment>
                <Input
                  value={formData.location}
                  placeholder='Location'
                  onChange={(e) => setFormField('location', e.target.value)}
                />
                <Input
                  value={formData.client}
                  placeholder='Client'
                  onChange={(e) => setFormField('client', e.target.value)}
                />
              </InputsWrapper>
              <ImageWrapper>
                <EditImageView
                  imageId={formData.imageId}
                  containerStyle={{
                    height: '12rem',
                    width: '11rem',
                  }}
                  placeholderStyles={{
                    plusIconSize: '1.25rem',
                    marginBetween: '0.375rem',
                    textSize: '0.75rem',
                  }}
                  onImageUpload={(resData) => {
                    setUploadedImagesIds((c) => [...c, resData]);
                    setFormField('imageId', resData);
                  }}
                  onImageRemove={(resData, imageId) => {
                    setFormField('imageId', null);
                  }}
                />
              </ImageWrapper>
            </Row>
          </div>
          <Row marginTop='2rem'>
            <InputsGrid>
              <GridItem>
                <Label>Oil Flowrate (bbld)</Label>
                <Input
                  value={formData.oilFlowRate}
                  placeholder=''
                  type='number'
                  onChange={(e) => setFormField('oilFlowRate', e.target.value)}
                />
              </GridItem>
              <GridItem>
                <Label>Gas Rate (MM scfd)</Label>
                <Input
                  value={formData.gasRate}
                  placeholder=''
                  type='number'
                  onChange={(e) => setFormField('gasRate', e.target.value)}
                />
              </GridItem>
              <GridItem>
                <Label>Water Injection (m3/d)</Label>
                <Input
                  value={formData.waterInjection}
                  placeholder=''
                  type='number'
                  onChange={(e) => setFormField('waterInjection', e.target.value)}
                />
              </GridItem>
              <GridItem>
                <Label>Gas Injection (y/n)</Label>
                <Checkbox
                  classes='checkbox'
                  value={formData.gasInjection}
                  onChange={(val) => setFormField('gasInjection', val)}
                />
              </GridItem>
              <GridItem>
                <Label>Gas Export (y/n)</Label>
                <Checkbox
                  classes='checkbox'
                  value={formData.gasExport}
                  onChange={(val) => setFormField('gasExport', val)}
                />
              </GridItem>
            </InputsGrid>
          </Row>
          <Row marginTop='2rem'>
            <GridItem>
              <Label>Start date :</Label>
              <MyDatePicker
                value={formData.date ? new Date(formData.date) : undefined}
                onChange={(date) => setFormField('date', date)}
              />
            </GridItem>
          </Row>
          <Row marginTop='2rem'>
            <Label>Notes:</Label>
          </Row>
          <NotesInput
            value={formData.notes}
            onChange={(e) => setFormField('notes', e.target.value)}
          />
          {isAdmin && (
            <ButtonsWrapper>
              {isEditMode ? (
                <>
                  <RemoveProjectBtn
                    title='Remove Project'
                    onClick={handleRemoveProject}
                  />
                  <StyledActionBtn
                    width='200px'
                    title='Update Project'
                    onClick={handleUpdateProject}
                  />
                </>
              ) : (
                <StyledActionBtn
                  width='200px'
                  title='Add Project'
                  disabled={!(formData.name && formData.number && projectNumberValid)}
                  onClick={handleAddProject}
                />
              )}
            </ButtonsWrapper>
          )}
        </ContentWrapper>
      </Wrapper>
    </Modal>
  );
};

export default CreateEditProjectModal;
